import { isBefore } from 'date-fns';
import { z } from 'zod';

import { isMAEContract } from '@/lib/contracts';
import { toDate, type DateInput } from '@/lib/dates';
import type { Contract } from '@/types/api/contract';

/**
 * Validation de la déclaration de sinistre (ancien `DeclarationSinistre/validate.js`).
 *
 * Dépend du contrat sélectionné : la date du sinistre ne peut précéder sa date d'effet.
 *
 * Les questions Oui/Non marquées obligatoires (*) le sont réellement : le composant radio n'a pas de
 * contrôle natif, seul ce schéma les impose. Celles de la seconde étape (constat, police, réparateur…)
 * ne sont vérifiées qu'une fois la première validée (`secondeEtape`) : avant, elles ne sont pas à
 * l'écran. Les conditions reprennent celles de leur affichage.
 */
export const createDeclarationSinistreSchema = (contract?: Contract, secondeEtape = false) =>
    z.looseObject({}).superRefine((values, ctx) => {
        const v = values as Record<string, unknown>;
        const errors: Record<string, string> = {};
        const cat = v.catsinorigin as string;
        const isMae = isMAEContract(contract);
        const requireField = (field: string, condition = true) => {
            if (condition && !v[field]) errors[field] = 'Le champ est obligatoire';
        };

        const requiredFields = ['catsinorigin', 'commentaire', 'datesurvorigin', 'heuresurvorigin', 'pays_lieu_sinistre', 'corpoconduct'];

        requiredFields.forEach((field) => {
            if (!v[field]) {
                errors[field] = 'Le champ est obligatoire';
            }
        });

        // Champs conditionnellement obligatoires
        if (v.catsinorigin && v.catsinorigin !== '10.0' && !v.vehiculeDommage) {
            errors.vehiculeDommage = 'Le champ est obligatoire';
        }

        if (['104.1', '104.4', '103.3'].includes(v.catsinorigin as string) && !v.indentify) {
            errors.indentify = 'Le champ est obligatoire';
        }

        // Sans 104.3 : la question des témoins n'est pas posée pour ce type de sinistre
        if (['103.1', '103.2', '103.3', '104.1', '104.2', '104.4'].includes(cat) && !v.temoin) {
            errors.temoin = 'Le champ est obligatoire';
        }

        requireField('equipementsDommage', !isMae && cat === '104.4');
        if (v.equipementsDommage === 'T') {
            ['Casque', 'Gants', 'Blouson', 'Bottes', 'Airbag', 'Autre'].forEach((equipement) =>
                requireField(`equipementsDommage${equipement}`),
            );
        }

        if (secondeEtape) {
            const avecConstat = ['103.1', '103.2', '103.3', '104.1', '104.3', '104.4'].includes(cat);
            requireField('constat_accident', avecConstat);
            requireField('constat_sign_tiers', avecConstat && v.constat_accident === 'T');
            requireField('constat_sign_assure', avecConstat && v.constat_accident === 'T');

            requireField(
                'intervention_police',
                (['103.1', '103.2', '103.3', '104.1', '104.2', '104.3', '104.4'].includes(cat) && v.constat_sign_tiers === 'F') ||
                    v.constat_sign_assure === 'F',
            );

            requireField('vehicule', !isMae && v.vehiculeDommage === 'T');
            requireField('remb_garage', v.vehicule === 'T' && v.vehiculeDommage === 'T' && cat !== '10.0');
        }

        // Date du sinistre >= date d'effet du contrat
        if (
            v.datesurvorigin &&
            contract?.situation?.dateEffetAFN &&
            isBefore(toDate(v.datesurvorigin as DateInput), toDate(contract.situation.dateEffetAFN))
        ) {
            errors.datesurvorigin = "La date du sinistre ne peut pas être antérieure à la date d'effet du contrat.";
        }

        // Email client
        if (v.client_email) {
            if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(String(v.client_email))) {
                errors.client_email = 'Adresse e-mail invalide';
            }
        }

        // Téléphone client
        if (v.client_phone) {
            if (!/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/i.test(String(v.client_phone))) {
                errors.client_phone = 'Numéro de téléphone invalide';
            }
        }

        // Mot de passe (quand les coordonnées changent)
        if (v.client_password && String(v.client_password).length < 6) {
            errors.client_password = 'Le mot de passe doit contenir au moins 6 caractères';
        }

        for (const [path, message] of Object.entries(errors)) ctx.addIssue({ code: 'custom', path: [path], message });
    });
